/**
 * The pure ability: `can(entitlement, id)`.
 *
 * No imports, no framework, no clock of its own — everything it reads is an argument. That is what
 * lets the same file live in zl-api, zl-app, zl-mobile and the educators app, and be tested in each
 * without a store, a request or a mocked Date.
 *
 * ## Why this is a lookup and not a policy engine
 *
 * By the time a check runs, every scope has already been resolved: a zoo-scoped purchase became
 * habitat ids while entitlements were computed, once, in zl-api. So the question here is a map
 * lookup, a `*` fallback and a date comparison — which is why there is no CASL (ADR 0009). If
 * conditions on the *object* ever arrive ("delete any media here unless it is pinned"), this is
 * where a library would go, behind the same `can`.
 *
 * ## This file is copied, not shared
 *
 * Three copies — here (which the educators app uses), zl-app and zl-mobile — and they must move
 * together, the way the Media Tag vocabulary already does. A name added in one app and nowhere else
 * checks something nobody grants.
 *
 * The same *behaviour*, not the same bytes: each repo formats to its own house style, and zl-app's
 * lint rules rule out `for..of` entirely. Compare what the copies do, not how they read.
 */

/** The payload the API sends: entitlements, with repeated id lists named once in `scopes`. */
export interface EntitlementsPayload {
  entitlements?: Record<string, Record<string, string | null>>;
  scopes?: Record<string, string[]>;
}

/**
 * The names `can` accepts, copied from zl-api `ENTITLEMENT_KINDS` — so a misspelt check fails to
 * compile instead of answering `false` (ADR 0009).
 */
export type Entitlement =
  | 'habitat.access'
  | 'habitat.host'
  | 'habitat.cameraMode'
  | 'habitat.streamPower'
  | 'habitat.editHabitatMeta'
  | 'habitat.cardsEdit'
  | 'habitat.lessonCard'
  | 'highlights.full'
  | 'highlights.pagination'
  | 'highlights.clipsPriority'
  | 'home.ambiance'
  | 'video.adFree'
  | 'media.upload'
  | 'media.editAnyCaption'
  | 'media.editDetails'
  | 'media.deleteAny'
  | 'media.downloadRaw'
  | 'media.downloadWatermarked'
  | 'media.globalSearch'
  | 'album.filters'
  | 'chat.hyperlinks'
  | 'chat.moderate'
  | 'event.create'
  | 'event.book'
  | 'schedule.filters'
  | 'resource.access'
  | 'classroom.nextUp'
  | 'tour.default'
  | 'tour.tzPlus'
  | 'analytics.partner';

/** Why a check failed, which is what decides whether to offer a plan or a renewal. */
export type Verdict = 'granted' | 'not-granted' | 'expired' | 'out-of-scope';

export interface Ability {
  can(entitlement: Entitlement, id?: string): boolean;
  explain(entitlement: Entitlement, id?: string): Verdict;
}

/** `*` — everywhere. A check passes on this or on the id, whichever is present. */
const EVERYWHERE = '*';

/** Grants of one entitlement: id → when it lapses, `null` meaning never. */
type Grants = Map<string, number | null>;

/**
 * `null` (never) wins; otherwise the later date does. Two keys can name the same id — a zoo's `@1`
 * and that habitat directly — and the grant that lasts longer is the one held. `laterExpiry` on the
 * server merges the same way.
 */
const laterOf = (held: number | null | undefined, at: number | null): number | null => {
  if (held === undefined) return at;
  if (held === null || at === null) return null;
  return Math.max(held, at);
};

export interface AbilityOptions {
  /** Injectable so expiry can be tested without mocking the clock, and so a render can pin one
   * moment rather than asking the machine per call. */
  now?: () => number;
}

/**
 * Expand the payload once, then answer from it.
 *
 * The expansion is the only work: `@1` references become their ids, dates become numbers. After
 * that `can` allocates nothing, so it is safe in a render loop or a list.
 */
export const createAbility = (payload: EntitlementsPayload | undefined, options: AbilityOptions = {}): Ability => {
  const now = options.now ?? (() => Date.now());
  const scopes = payload?.scopes ?? {};
  const byName = new Map<string, Grants>();

  Object.entries(payload?.entitlements ?? {}).forEach(([name, grants]) => {
    const map: Grants = new Map();
    Object.entries(grants ?? {}).forEach(([key, expiry]) => {
      const at = expiry === null ? null : Date.parse(expiry);
      // An unparseable date is treated as no grant rather than as "never": a malformed payload
      // should lose somebody a button, not hand them one for ever.
      if (at !== null && Number.isNaN(at)) return;
      const ids = key === EVERYWHERE ? [EVERYWHERE] : scopes[key] ?? [key];
      ids.forEach((id) => map.set(id, laterOf(map.get(id), at)));
    });
    byName.set(name, map);
  });

  const live = (expiry: number | null | undefined): boolean => expiry === null || (expiry !== undefined && expiry > now());

  const explain = (entitlement: Entitlement, id?: string): Verdict => {
    const grants = byName.get(entitlement);
    if (!grants || grants.size === 0) return 'not-granted';

    const everywhere = grants.get(EVERYWHERE);
    if (everywhere !== undefined && live(everywhere)) return 'granted';

    if (id !== undefined) {
      const forId = grants.get(id);
      if (forId !== undefined) return live(forId) ? 'granted' : 'expired';
      // They hold this entitlement, but not here. Not the same as never having held it: the upsell
      // is a different one, and so is the sentence.
      return everywhere === undefined ? 'out-of-scope' : 'expired';
    }

    // Asked without an id about something that is granted per id: whether they hold it *somewhere*
    // is the only answerable question, and holding it somewhere is not holding it here.
    return everywhere === undefined ? 'out-of-scope' : 'expired';
  };

  return {
    can: (entitlement, id) => explain(entitlement, id) === 'granted',
    explain,
  };
};
